/* eslint-disable */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';
(pdfMake as any).vfs = (pdfFonts as any).vfs;

export interface IComprovanteBaixaConta {
  numero: string;
  parcela?: string;
  valor: number;
}

export interface IComprovanteBaixaFormaPagamento {
  nome: string;
  valor: number;
}

export interface IComprovanteBaixaDados {
  parceiro: string;
  contaNome: string;
  datalan: Date;
  contas: IComprovanteBaixaConta[];
  formasPagamento: IComprovanteBaixaFormaPagamento[];
  totalBaixado: number;
  desconto: number;
  acrescimo: number;
  logo?: string;
}

// Mesmo carregamento de logo (com fallback gerado em canvas) de
// ImpressaoReceber.ts — repetido aqui em vez de importado porque aquele
// arquivo já chama `pdfMake.createPdf(...).open()` direto, e aqui a gente
// precisa do Blob (pro visualizador com opção Caixa/Térmica, ver
// DialogVisualizarComprovante.tsx).
async function getBase64ImageFromUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = function () {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Erro canvas');
      canvas.height = img.naturalHeight;
      canvas.width = img.naturalWidth;
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject('Erro ao carregar logo');
    img.src = url;
  });
}

function gerarLogoPadrao(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 90;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const gradient = ctx.createLinearGradient(0, 0, 320, 0);
  gradient.addColorStop(0, '#1f2937');
  gradient.addColorStop(1, '#374151');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.beginPath();
  ctx.arc(45, 45, 25, 0, 2 * Math.PI);
  ctx.fillStyle = '#10b981';
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 20px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('COMPROVANTE', 90, 45);
  return canvas.toDataURL('image/png');
}

const formatarMoeda = (valor: number) =>
  Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Comprovante de uma baixa de Contas a Receber — gerado inteiramente com o
// que o DialogBaixa.tsx já tem em mãos no momento da confirmação (contas
// selecionadas + formas de pagamento usadas), sem chamada nova ao backend.
export async function gerarComprovanteBaixa(dados: IComprovanteBaixaDados): Promise<Blob> {
  let imageData: string;
  if (dados.logo && dados.logo.trim() !== '') {
    const baseUrl = (window as any).env?.REACT_APP_URL_IMGLOG || '';
    const imageUrl = baseUrl + dados.logo;
    try {
      imageData = await getBase64ImageFromUrl(imageUrl);
    } catch {
      imageData = gerarLogoPadrao();
    }
  } else {
    imageData = gerarLogoPadrao();
  }

  const documentDefinition: any = {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 60],

    footer: function (currentPage: number, pageCount: number) {
      return {
        stack: [
          {
            canvas: [
              { type: 'line', x1: 40, y1: 0, x2: 555, y2: 0, lineWidth: 0.5, lineColor: '#e5e7eb' },
            ],
          },
          {
            text: `Página ${currentPage} de ${pageCount}`,
            alignment: 'center',
            fontSize: 8,
            margin: [0, 5, 0, 0],
            color: '#6b7280',
          },
        ],
      };
    },

    styles: {
      titulo: { fontSize: 16, bold: true, alignment: 'center' },
      subInfo: { fontSize: 10 },
      tableHeader: { bold: true, fontSize: 9, color: 'white', fillColor: '#1f2937', alignment: 'center' },
      tableCell: { fontSize: 9 },
      totalFinal: { bold: true, alignment: 'right', fillColor: '#d1d5db' },
    },

    content: [
      {
        columns: [
          { image: imageData, width: 60, alignment: 'left' },
          { text: 'COMPROVANTE DE RECEBIMENTO', style: 'titulo', alignment: 'center', width: '*' },
          { text: '', width: 60 },
        ],
        margin: [0, 0, 0, 15],
        columnGap: 10,
      },
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#1f2937' }],
        margin: [0, 0, 0, 10],
      },
      {
        columns: [
          { text: `Parceiro: ${dados.parceiro}`, style: 'subInfo' },
          { text: `Conta: ${dados.contaNome}`, style: 'subInfo' },
          { text: `Data: ${moment(dados.datalan).format('DD/MM/YYYY')}`, alignment: 'right', style: 'subInfo' },
        ],
        margin: [0, 0, 0, 15],
      },

      { text: 'Contas baixadas', bold: true, fontSize: 10, margin: [0, 0, 0, 4] },
      {
        table: {
          headerRows: 1,
          widths: ['*', 80, 90],
          body: [
            [
              { text: 'Número', style: 'tableHeader' },
              { text: 'Parcela', style: 'tableHeader' },
              { text: 'Valor', style: 'tableHeader' },
            ],
            ...dados.contas.map((conta) => [
              { text: conta.numero || '***', style: 'tableCell' },
              { text: conta.parcela || '—', alignment: 'center', style: 'tableCell' },
              { text: formatarMoeda(conta.valor), alignment: 'right', style: 'tableCell' },
            ]),
          ],
        },
        layout: {
          fillColor: (rowIndex: number) => (rowIndex === 0 ? null : rowIndex % 2 === 0 ? '#f9fafb' : null),
          paddingTop: () => 5,
          paddingBottom: () => 5,
          hLineWidth: (i: number) => (i === 1 ? 1 : 0.5),
          vLineWidth: () => 0,
          hLineColor: () => '#e5e7eb',
        },
        margin: [0, 0, 0, 15],
      },

      { text: 'Formas de pagamento', bold: true, fontSize: 10, margin: [0, 0, 0, 4] },
      {
        table: {
          headerRows: 1,
          widths: ['*', 90],
          body: [
            [
              { text: 'Forma', style: 'tableHeader' },
              { text: 'Valor', style: 'tableHeader' },
            ],
            ...dados.formasPagamento.map((forma) => [
              { text: forma.nome, style: 'tableCell' },
              { text: formatarMoeda(forma.valor), alignment: 'right', style: 'tableCell' },
            ]),
          ],
        },
        layout: {
          fillColor: (rowIndex: number) => (rowIndex === 0 ? null : rowIndex % 2 === 0 ? '#f9fafb' : null),
          paddingTop: () => 5,
          paddingBottom: () => 5,
          hLineWidth: (i: number) => (i === 1 ? 1 : 0.5),
          vLineWidth: () => 0,
          hLineColor: () => '#e5e7eb',
        },
        margin: [0, 0, 0, 15],
      },

      {
        columns: [
          { text: `Acréscimo: R$ ${formatarMoeda(dados.acrescimo)}`, style: 'subInfo' },
          { text: `Desconto: R$ ${formatarMoeda(dados.desconto)}`, style: 'subInfo', alignment: 'center' },
          { text: `Total recebido: R$ ${formatarMoeda(dados.totalBaixado)}`, style: 'totalFinal', alignment: 'right' },
        ],
        margin: [0, 0, 0, 20],
      },

      { text: 'Comprovante de recebimento — não é documento fiscal', alignment: 'center', fontSize: 8, color: '#6b7280' },
      {
        text: `Gerado em ${moment().format('DD/MM/YYYY HH:mm')}`,
        alignment: 'right',
        fontSize: 8,
        margin: [0, 10, 0, 0],
        color: '#6b7280',
      },
    ],
  };

  return new Promise((resolve, reject) => {
    try {
      const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
      pdfDocGenerator.getBlob((blob: Blob) => resolve(blob));
    } catch (error) {
      reject(error);
    }
  });
}
